<!--
  INFO:
    The ordered list component displays a list of selectable items, with the possibility to
    lock some of them or change the order of the ones not locked.

    Font family and size will be inherited from the page. It works together with the OrderedItem component.

  PROPS:
    showCheckboxes: Boolean. Whether the list will be selectable or not. If set as true, the list is only re-orderable.
                    Default value is false.
    items: Array of objects. Each object is a item of the list and contains its state, e.g.

      [
        {
          code: 'SIB',
          value: 'Siblings already enrolled at the school',
          locked: true,
          selected: null
        },
        ...
      ]

    code: STRING | the key of each item in the list.
    value: STRING | the text displayed for that item.
    locked: BOOLEAN | whether the item is locked or not. If an icon is locked, it cannot be selected or moved.
    order: NUMBER | the current order of the item. It changes according to the user interaction.
    selected: BOOLEAN | whether the item is selected or not. Initial value should be null. [OPTIONAL]

  EVENTS:
    @change(val) - event triggered when the checkbox is clicked to select or unselect an item. It modifies the current
    state of that item.

  SLOTS:
    item: slot for the text displayed in each item and its label. Receive item as prop.
    checkbox: slot for the checkbox in the selectable items.
    lockedIcon: slot for the locked icon in the locked items.
    moveUpButton: slot for the move up arrow button at the end of each row.
-->

<template>
  <v-card v-if="list && list.length">
    <v-list
      class="pa-0"
      data-testid="ordered-list"
    >
      <v-list-item
        v-for="(item, i) in list"
        :key="item.code"
        class="row"
        :class="{ active: isActive(item) }"
      >
        <div
          v-if="!item.locked"
          class="d-flex align-center"
        >
          <slot
            name="checkbox"
            :item="item"
          >
            <v-checkbox
              v-if="!item.locked && showCheckboxes"
              v-model="item.selected"
              class="pa-0"
              :aria-label="`include ${item.value}`"
              @change="val => selectionChanged(val, i)"
              data-testid="row-checkbox"
            />
          </slot>
          <div
            class="spacer"
            v-if="!showCheckboxes"
          />
          <slot
            name="item"
            :item="item"
          >
            <OrderedItem
              :number="isActive(item) ? i + 1 : null"
              :text="item.value"
            />
          </slot>
        </div>
        <div
          v-if="item.locked"
          class="d-flex align-center"
        >
          <slot name="lockedIcon">
            <v-icon class="mr-2">
              mdi-lock-outline
            </v-icon>
          </slot>
          <slot
            name="item"
            :item="item"
          >
            <OrderedItem
              :number="i + 1"
              :text="item.value"
            />
          </slot>
        </div>
        <v-spacer />
        <slot
          name="moveUpButton"
          :item="item"
        >
          <v-btn
            v-if="(item.selected || !showCheckboxes) && !item.locked"
            icon
            class="reorder-button"
            @click="moveUp(i)"
            :disabled="isFirstMovable(i)"
            :aria-label="`move ${item.value} up`"
          >
            <v-icon>mdi-arrow-up</v-icon>
          </v-btn>
        </slot>
      </v-list-item>
    </v-list>
  </v-card>
</template>

<script>
import OrderedItem from './OrderedItem'

export default {
  name: 'OrderedList',
  components: {
    OrderedItem
  },
  props: {
    items: {
      type: Array,
      default: () => [],
    },
    showCheckboxes: {
      type: Boolean,
      default: false
    },
  },
  data() {
    return {
      list: this.items,
    }
  },
  methods: {
    isActive(item) {
      return item.selected || item.locked || !this.showCheckboxes
    },
    isFirstMovable(index) {
      return (
        this.list.find(c => !c.locked && (c.selected || !this.showCheckboxes)) ===
        this.list[index]
      )
    },
    selectionChanged(val, i) {
      const item = this.list[i]
      this.list.splice(i, 1)
      const selectedCount = this.list.filter(e => e.selected).length
      const unmovableCount = this.list.filter(e => e.locked).length
      if (val) {
        // move to the bottom of the selected list, which starts after the locked items
        this.list.splice(selectedCount + unmovableCount,
          0,
          item)
      } else {
        // move to the top of unselected list
        this.list.splice(
          selectedCount + unmovableCount,
          0,
          item
        )
      }
    },
    moveUp(i) {
      const item = this.list[i]
      this.list.splice(i, 1)
      this.list.splice(i - 1, 0, item)
    }
  },
  created() {
    // sort the list to move locked items up
    let locked = this.list.filter(e => e.locked)
    let unlocked = this.list.filter(e => !e.locked)
    this.list = locked.concat(unlocked)
  }
}
</script>

<style lang="scss" scoped>
@import '../../styles/variables.scss';

.row {
  min-height: 72px !important;
  min-width: 100%;
  padding: 0 24px;
  margin: 0;

  &:not(:last-child) {
    border-bottom: 1px solid $ads-light-20;
  }

  &:not(.active) {
    background: $ads-light-10;
    border-radius: 4px;

    .v-label {
      color: $ads-dark-80;
    }
  }

  &.active {
    background-color: $ads-white;
  }
}

.reorder-button {
  width: 48px !important;
  height: 48px !important;
  border: none;

  &.v-btn.v-btn--disabled {
    color: $ads-light-40 !important;
    opacity: 1;
  }
}

.spacer {
  width: 32px;
}
</style>
